import { useEffect, useState } from "react";
import { BookHeart, Trash2, Flame } from "lucide-react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";

function formatDate(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function Insights() {
    const [insights, setInsights] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/insights");
            setInsights(r.data || []);
        } catch (e) {
            toast.error("Could not load your vault.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        try {
            await api.delete(`/insights/${id}`);
            setInsights((arr) => arr.filter((i) => i.insight_id !== id));
            toast.success("Released.");
        } catch (e) {
            toast.error("Could not delete.");
        }
    };

    return (
        <div className="max-w-4xl mx-auto w-full px-6 md:px-10 py-10 md:py-14" data-testid="insights-page">
            <div className="mb-10 pl-12 md:pl-0">
                <div className="flex items-center gap-2 text-brand-charcoal/60 text-xs uppercase tracking-widest">
                    <BookHeart className="w-3 h-3 text-brand-spark" strokeWidth={2} />
                    Wisdom vault
                </div>
                <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-brand-darkPurple leading-tight tracking-tight">
                    The lines you kept.
                </h1>
                <p className="mt-3 text-brand-charcoal/75 max-w-2xl leading-relaxed">
                    Everything you saved from a conversation lives here. Come back when you forget your own wisdom.
                </p>
            </div>

            {loading ? (
                <div className="text-brand-darkPurple/60 font-serif italic">gathering…</div>
            ) : insights.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-brand-darkPurple/15 bg-white/50 p-10 text-center" data-testid="insights-empty">
                    <Flame className="w-6 h-6 text-brand-spark mx-auto mb-3" strokeWidth={1.5} />
                    <div className="font-serif italic text-xl text-brand-darkPurple">Nothing saved yet.</div>
                    <p className="text-sm text-brand-charcoal/70 mt-2 max-w-md mx-auto">
                        When something the companion says catches you, tap <em>Save as insight</em> to keep it here.
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {insights.map((ins) => (
                        <article
                            key={ins.insight_id}
                            data-testid={`insight-${ins.insight_id}`}
                            className="group rounded-2xl bg-white border border-brand-darkPurple/10 shadow-soft-card p-6 md:p-7"
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                    <div className="font-serif text-brand-darkPurple leading-relaxed text-lg whitespace-pre-wrap">
                                        &ldquo;{ins.text}&rdquo;
                                    </div>
                                    <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-brand-charcoal/55">
                                        <span>{formatDate(ins.created_at)}</span>
                                        {ins.tags?.length > 0 && ins.tags.map((t) => (
                                            <span key={t} className="px-2 py-0.5 rounded-full bg-brand-creamDeep text-brand-darkPurple/80 border border-brand-darkPurple/8">
                                                {t.replace(/_/g, " ")}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <button
                                    data-testid={`delete-insight-${ins.insight_id}`}
                                    onClick={() => remove(ins.insight_id)}
                                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-brand-charcoal/50 hover:text-red-500 transition-opacity p-1"
                                    aria-label="Delete insight"
                                >
                                    <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                                </button>
                            </div>
                        </article>
                    ))}
                </div>
            )}
        </div>
    );
}
